import { Box, Button, Dialog, Input, Typography } from '@robocorp/components';
import {
  ChangeEvent,
  FC,
  Dispatch,
  createContext,
  SetStateAction,
  useContext,
  useCallback,
  ReactNode,
  useState,
  DependencyList,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';

export type ConfirmProps = {
  title?: string;
  text: React.ReactNode;
  confirmActionText?: string;
  onConfirm?: ConfirmCallback;
  hardConfirm?: string;
  isConfirming?: boolean;
};

type ConfirmCallback = () => void | Promise<unknown>;

export type ConfirmContextType = [
  ConfirmProps | null,
  Dispatch<SetStateAction<ConfirmProps | null>>,
];
export const ConfirmContext = createContext<ConfirmContextType>([null, () => null]);

export const useConfirm = (
  props: Omit<ConfirmProps, 'onConfirm'>,
  dependencies: DependencyList,
) => {
  const dialogContext = useContext(ConfirmContext);

  const confirm = useCallback(
    (onConfirm: ConfirmCallback) => () => {
      dialogContext[1]({ ...props, onConfirm });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies,
  );

  return confirm;
};

export type ConfirmActionProps = Omit<ConfirmProps, 'onConfirm'>;
export const useConfirmAction = () => {
  const dialogContext = useContext(ConfirmContext);

  const confirm = useCallback(
    (onConfirm: ConfirmCallback, props: ConfirmActionProps) => () => {
      dialogContext[1]({ ...props, onConfirm });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return confirm;
};

type ConformDialogProps = {
  open: boolean;
  onClose: () => void;
} & Partial<ConfirmProps>;

export const ConfirmDialog: FC<ConformDialogProps> = ({
  open,
  title,
  text,
  hardConfirm,
  isConfirming,
  confirmActionText,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const [confirmValue, setConfirmValue] = useState<string>('');
  const onChangeText = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmValue(event.target.value);
  }, []);

  const onCloseHandler = useCallback(() => {
    setConfirmValue('');
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  return (
    <Dialog open={open} onClose={onCloseHandler}>
      <Dialog.Header>
        <Dialog.Header.Title title={title ?? t('confirmationDialog.title')} />
        <Dialog.Header.Description>{text}</Dialog.Header.Description>
      </Dialog.Header>
      <Dialog.Content>
        {hardConfirm && (
          <Box pb="$4">
            <Typography fontWeight="regular" mb="$12">
              <Trans
                i18nKey="hardConfirmExplain"
                values={{ confirmPhrase: hardConfirm }}
                components={{ strong: <Typography as="div" pt="$8" fontWeight="medium" /> }}
              />
            </Typography>
            <Input
              value={confirmValue}
              onChange={onChangeText}
              error={confirmValue.length > 0 && confirmValue !== hardConfirm}
              autoFocus
              aria-label={t('hardConfirmExplain', { confirmPhrase: hardConfirm }) || ''}
            />
          </Box>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          data-testid="dialog__confirm-submit"
          loading={isConfirming}
          onClick={onConfirm}
          disabled={isConfirming || (!!hardConfirm && confirmValue !== hardConfirm)}
          variant="primary"
        >
          {confirmActionText || t('confirm')}
        </Button>
        <Button onClick={onCloseHandler} variant="secondary" disabled={isConfirming}>
          {t('cancel')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export const ConfirmProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const confirmContextValue = useState<ConfirmProps | null>(null);
  const dialogProps = confirmContextValue[0];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onClose = useCallback(() => confirmContextValue[1](null), []);

  const onConfirm = useCallback(async () => {
    if (dialogProps?.onConfirm) {
      setIsConfirming(true);
      try {
        await dialogProps.onConfirm();
      } catch {
        // stop loading state, but don't close dialog to indicate that failure was detected
        setIsConfirming(false);
        return;
      }
      setIsConfirming(false);
    }

    onClose();
  }, [dialogProps, onClose]);

  const isOpen = confirmContextValue[0] !== null;

  return (
    <ConfirmContext.Provider value={confirmContextValue}>
      <ConfirmDialog
        key={String(isOpen)}
        isConfirming={isConfirming}
        open={isOpen}
        title={dialogProps?.title}
        text={dialogProps?.text}
        hardConfirm={dialogProps?.hardConfirm}
        confirmActionText={dialogProps?.confirmActionText}
        onClose={onClose}
        onConfirm={onConfirm}
      />
      {children}
    </ConfirmContext.Provider>
  );
};

export const useConfirmDialogAction = (): [
  (callbackAction: () => void) => () => void,
  Pick<ConformDialogProps, 'open' | 'onClose' | 'onConfirm'>,
] => {
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<() => void | null>();

  const onOpenConfirmDialog = useCallback((callbackAction: () => void) => {
    setAction(() => callbackAction);
    return () => setOpen(true);
  }, []);

  const onConfirm = () => {
    if (action instanceof Function) {
      action();
    }
    setOpen(false);
  };

  const onClose = () => setOpen(false);

  return [
    onOpenConfirmDialog,
    {
      open,
      onClose,
      onConfirm,
    },
  ];
};
